/* eslint-disable max-lines */
import { useAuth0 } from '@auth0/auth0-react';
import {
    CONTRACT_RATE,
    FMH_NPI,
    ReferentPriceTypeOptions,
    ReferentPriceTypes,
    RouteCodeOptions,
    RouteCodes,
} from '@strata/constants';
import { Checkbox } from '@strata/react-checkbox-list';
import { ReasonInput } from '@strata/react-compound-inputs';
import { FormContainer } from '@strata/react-form-container';
import { FormHeaderAndContent } from '@strata/react-form-header-and-content';
import { FullscreenLoading } from '@strata/react-fullscreen-loading';
import { InlineMessage } from '@strata/react-inline-message';
import { BidInput, TextAreaFormik } from '@strata/react-inputs';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { ErrorToast, SuccessToast } from '@strata/react-toast-message';
import { validationStrings } from '@strata/strata-ui-strings';
import Decimal from 'decimal.js';
import { Formik } from 'formik';
import * as Yup from 'yup';

import { transferTreatment } from 'api/treatmentAPI';
import { UNSPECIFIED_NPI } from 'services/constants';
import { handleError } from 'services/Error';
import { useLazyPostDetermineReferralRatesQuery } from 'store/api/rateSetterApi';
import { useGetAllSuppliersQuery, useGetPayersQuery } from 'store/api/referenceApi';
import { useLazyGetTreatmentDetailsQuery } from 'store/api/treatmentApi';

const isFmhSupplier = (supplierNpi) => supplierNpi === FMH_NPI || supplierNpi === UNSPECIFIED_NPI;

const resolveTransferPricing = async ({ treatmentDetails, determineReferralRates }) => {
    const existingPriceSource = treatmentDetails.activeReferral?.priceSource;
    const existingIsFrozen = treatmentDetails.activeReferral?.priceIsFrozen;
    const existingPricing = {
        priceSource: existingPriceSource,
        isFrozen: existingIsFrozen,
    };

    if (existingPriceSource != null && existingIsFrozen != null) {
        return existingPricing;
    }

    try {
        const rateRule = await determineReferralRates({
            payerId: treatmentDetails.payerId,
            segmentId: treatmentDetails.segmentId,
            productServiceOid: treatmentDetails.productServiceOid,
            benefit:
                treatmentDetails.activeReferral?.benefitTypeRaw ?? treatmentDetails.benefitType,
            isBrand: treatmentDetails.isBrand,
            memberId: treatmentDetails.memberDetails?.memberId,
            memberFirstName: treatmentDetails.memberDetails?.firstName,
            memberDateOfBirth: treatmentDetails.memberDetails?.dateOfBirth,
            routeCode: RouteCodes.ROFR,
            placeOfService: treatmentDetails.placeOfService,
            lineOfBusiness: treatmentDetails.memberDetails?.lineOfBusiness,
        }).unwrap();

        return {
            priceSource: rateRule?.priceSource ?? existingPriceSource,
            isFrozen: rateRule?.isFrozen ?? existingIsFrozen,
        };
    } catch {
        return existingPricing;
    }
};

const MethodOptions = RouteCodeOptions.filter(({ value }) =>
    [RouteCodes.DIRECT, RouteCodes.ROFR].includes(value),
);

const ReasonOptions = [
    'Contractual',
    'Drug access change',
    'Out of network pharmacy',
    'State capability change',
];

export const BulkTransfer = () => {
    const { user } = useAuth0();
    const { data, isFetching } = useGetPayersQuery();
    const { payerList } = data ?? {};

    const { data: allSuppliersData, isFetching: allSuppliersFetching } = useGetAllSuppliersQuery();
    const { supplierList } = allSuppliersData ?? {};

    const [getTreatmentDetails] = useLazyGetTreatmentDetailsQuery();
    const [determineReferralRates] = useLazyPostDetermineReferralRatesQuery();

    return (
        <FormHeaderAndContent
            mainContent={
                <Formik
                    initialValues={{}}
                    validationSchema={Yup.object({
                        payerId: Yup.string().required(
                            validationStrings.requiredField.errorMessage,
                        ),
                        treatmentIds: Yup.string()
                            .required(validationStrings.requiredField.errorMessage)
                            .matches(/^.+$/gm, 'IDs formatted incorrectly.'),
                        supplierNpi: Yup.string().required(
                            validationStrings.requiredField.errorMessage,
                        ),
                        routeCode: Yup.string()
                            .oneOf(Object.values(RouteCodes))
                            .when('supplierNpi', {
                                is: (supplierNpi) => !isFmhSupplier(supplierNpi),
                                then: (schema) =>
                                    schema.required(validationStrings.requiredField.errorMessage),
                            }),
                        referentPriceType: Yup.string().oneOf(Object.values(ReferentPriceTypes)),
                        forceTransferRoute: Yup.bool(),
                        rateOverride: BidInput.validator().when('referentPriceType', {
                            is: (referentPriceType) => !!referentPriceType,
                            then: (schema) =>
                                schema.required(validationStrings.requiredField.errorMessage),
                        }),
                        useContractRate: Yup.bool(),
                        ...ReasonInput.validationSchema,
                    })}
                    onSubmit={async (values, { resetForm }) => {
                        try {
                            const errors = (
                                await Promise.all(
                                    values.treatmentIds?.match(/^.+$/gm).map((treatmentId) =>
                                        (async () => {
                                            try {
                                                const offerRate = (() => {
                                                    if (values.useContractRate) {
                                                        return CONTRACT_RATE;
                                                    }
                                                    return typeof values.rateOverride === 'number'
                                                        ? Decimal.div(
                                                              values.rateOverride,
                                                              100,
                                                          ).toNumber()
                                                        : null;
                                                })();

                                                const treatmentDetails = await getTreatmentDetails({
                                                    payerId: values.payerId,
                                                    treatmentId: treatmentId.trim(),
                                                }).unwrap();

                                                const { priceSource, isFrozen } =
                                                    await resolveTransferPricing({
                                                        treatmentDetails,
                                                        determineReferralRates,
                                                    });

                                                await transferTreatment({
                                                    payerId: values.payerId,
                                                    treatmentId: treatmentId.trim(),
                                                    newServicingPharmacyNpi: values.supplierNpi,
                                                    offerRate,
                                                    forceTransferRoute: values?.forceTransferRoute,
                                                    referentPriceType: values?.referentPriceType,
                                                    routeCode: values.routeCode,
                                                    priceSource,
                                                    isFrozen,
                                                    username: user?.name,
                                                    ...ReasonInput.submitValues(values),
                                                });

                                                return undefined;
                                            } catch {
                                                return treatmentId;
                                            }
                                        })(),
                                    ),
                                )
                            ).filter((err) => err);

                            if (errors.length > 0) {
                                ErrorToast({
                                    message: (
                                        <div>
                                            <div>
                                                The following treatments could not be transferred:
                                            </div>
                                            {errors.map((id) => (
                                                <div key={id}>{id}</div>
                                            ))}
                                        </div>
                                    ),
                                    timeout: 0,
                                });
                            } else {
                                SuccessToast({ message: 'Referrals transferred successfully.' });
                                resetForm();
                            }
                        } catch (err) {
                            handleError(
                                'There was an error attempting this action, please try again.',
                                err,
                            );
                        }
                    }}
                >
                    {(formik) => (
                        <FormContainer
                            formTitle="Bulk Transfer"
                            primaryButtonLabel="Transfer Referrals"
                            onFormSubmit={formik.handleSubmit}
                            onCancelClick={formik.handleReset}
                            isSubmitting={formik.isSubmitting || !(formik.isValid && formik.dirty)}
                            formLeftContent={
                                <>
                                    <FullscreenLoading
                                        isShowing={formik.isSubmitting}
                                        loadingText="Transferring referrals..."
                                    />
                                    <div className="margin__bottom--medium">
                                        <InlineMessage
                                            intent={InlineMessage.Intent.INFO}
                                            wrapText
                                            label="This action will send the active referral for a treatment to the selected pharmacy with an optional override rate."
                                        />
                                    </div>
                                    <SuggestSingleSelectFormik
                                        id="payerId"
                                        label="Health Plan"
                                        required
                                        isLoading={isFetching}
                                        values={payerList}
                                        selectedItem={payerList?.find(
                                            ({ value }) => value === formik.values.payerId,
                                        )}
                                        onChange={(newValue) =>
                                            formik.setFieldValue('payerId', newValue?.value)
                                        }
                                    />
                                    <TextAreaFormik
                                        label="Treatment IDs"
                                        id="treatmentIds"
                                        helperText="One ID per line."
                                        required
                                    />
                                    <SuggestSingleSelectFormik
                                        id="supplierNpi"
                                        label="Pharmacy"
                                        required
                                        values={supplierList}
                                        isLoading={allSuppliersFetching}
                                        selectedItem={supplierList?.find(
                                            ({ npi }) => npi === formik.values.supplierNpi,
                                        )}
                                        onChange={(newValue) =>
                                            formik.setValues({
                                                ...formik.values,
                                                supplierNpi: newValue?.npi,
                                                routeCode: RouteCodes.ROFR,
                                                referentPriceType: undefined,
                                                rateOverride: undefined,
                                                useContractRate: undefined,
                                            })
                                        }
                                    />
                                    {formik.values.supplierNpi &&
                                        !isFmhSupplier(formik.values.supplierNpi) && (
                                            <>
                                                <SuggestSingleSelectFormik
                                                    id="routeCode"
                                                    label="Method"
                                                    required
                                                    values={MethodOptions}
                                                    selectedItem={MethodOptions.find(
                                                        ({ value }) =>
                                                            value === formik.values.routeCode,
                                                    )}
                                                    onChange={(newValue) =>
                                                        formik.setValues({
                                                            ...formik.values,
                                                            routeCode: newValue?.value,
                                                            referentPriceType: undefined,
                                                            rateOverride: undefined,
                                                            useContractRate: undefined,
                                                        })
                                                    }
                                                />
                                                {!formik.values.useContractRate && (
                                                    <>
                                                        <SuggestSingleSelectFormik
                                                            id="referentPriceType"
                                                            label="Referent"
                                                            values={ReferentPriceTypeOptions}
                                                            selectedItem={ReferentPriceTypeOptions.find(
                                                                ({ value }) =>
                                                                    value ===
                                                                    formik.values.referentPriceType,
                                                            )}
                                                            onChange={(newValue) =>
                                                                formik.setValues({
                                                                    ...formik.values,
                                                                    referentPriceType:
                                                                        newValue?.value,
                                                                    rateOverride: undefined,
                                                                })
                                                            }
                                                        />
                                                        <BidInput
                                                            id="rateOverride"
                                                            label="Offer Rate Override"
                                                            helperText="This will override the offer rate, unless rate entered is less competitive than the Place of Service Override Rate."
                                                            required={
                                                                !!formik.values.referentPriceType
                                                            }
                                                            referentPriceType={
                                                                formik.values.referentPriceType
                                                            }
                                                        />
                                                        <Checkbox
                                                            id="forceTransferRoute"
                                                            label="Ignore Routing"
                                                            onChange={async (newValue) => {
                                                                await formik.setFieldValue(
                                                                    'forceTransferRoute',
                                                                    newValue,
                                                                );
                                                            }}
                                                            checked={
                                                                formik.values.forceTransferRoute ??
                                                                false
                                                            }
                                                        />
                                                    </>
                                                )}
                                                <Checkbox
                                                    id="useContractRate"
                                                    label="Use Contract Rate"
                                                    onChange={(checked) =>
                                                        formik.setValues({
                                                            ...formik.values,
                                                            useContractRate: checked,
                                                            referentPriceType: undefined,
                                                            rateOverride: undefined,
                                                        })
                                                    }
                                                    checked={formik.values.useContractRate}
                                                />
                                            </>
                                        )}
                                    <ReasonInput reasons={ReasonOptions} />
                                </>
                            }
                        />
                    )}
                </Formik>
            }
        />
    );
};
